import React, { useState, useEffect } from 'react';
import { X, CreditCard, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useCredits } from '../hooks/useCredits';

// Facebook Pixel TypeScript declaration
declare global {
  interface Window {
    fbq?: (command: string, eventName: string, params?: Record<string, unknown>) => void;
  }
}

interface PurchaseCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileCount?: number;
  creditsNeeded?: number;
  totalSizeBytes?: number;
}

// Credit pricing constants (matches backend and calculator)
const CREDIT_PRICE_USD = 0.001; // $0.001 per credit
const MIN_CREDITS = 1000; // Minimum 1000 credits
const MIN_PURCHASE_USD = 1.0; // $1.00 minimum

interface CreditCalculation {
  fileCount: number;
  creditsNeeded: number; // Total credits needed for the job
  currentCredits: number; // User's existing credits
  creditsAfterDeduction: number; // Credits remaining after applying current balance
  creditsToPurchase: number; // Actual credits to purchase (min 1,000 if purchasing)
  costInCents: number;
  costUSD: string;
  pricePerCredit: number;
  minimumPurchase: number;
  minimumEnforced: boolean;
  hasSufficientCredits: boolean; // Whether user already has enough credits
}

const PurchaseCreditsModal: React.FC<PurchaseCreditsModalProps> = ({
  isOpen,
  onClose,
  fileCount = 0,
  creditsNeeded: _creditsNeeded = 0,
  totalSizeBytes = 0,
}) => {
  const { user, isSignedIn, isLoaded } = useUser();
  const { redirectToSignUp } = useClerk();
  const { balance: currentCredits } = useCredits();
  const [calculation, setCalculation] = useState<CreditCalculation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  // Redirect to sign up if user is not authenticated
  useEffect(() => {
    if (isOpen && isLoaded && !isSignedIn) {
      console.log('❌ User not authenticated, redirecting to sign up');
      redirectToSignUp({
        redirectUrl: window.location.href, // Return to current page after sign up
      });
    }
  }, [isOpen, isLoaded, isSignedIn, redirectToSignUp]);

  // Calculate credits locally with current balance deduction
  useEffect(() => {
    if (isOpen && fileCount) {
      // 1 credit per image
      const creditsNeeded = fileCount;

      // Calculate credits after deducting current balance
      const creditsAfterDeduction = Math.max(0, creditsNeeded - currentCredits);

      // Check if user already has enough credits
      const hasSufficientCredits = currentCredits >= creditsNeeded;

      // If purchasing, enforce minimum credits
      const creditsToPurchase = hasSufficientCredits ? 0 : Math.max(creditsAfterDeduction, MIN_CREDITS);

      // Calculate cost (enforce minimum $1.00 if purchasing)
      const calculatedCost = creditsToPurchase * CREDIT_PRICE_USD;
      const totalCost = creditsToPurchase > 0 ? Math.max(calculatedCost, MIN_PURCHASE_USD) : 0;
      const costInCents = Math.round(totalCost * 100);

      // Check if minimum is being applied
      const minimumEnforced = creditsToPurchase === MIN_CREDITS && creditsAfterDeduction < MIN_CREDITS && creditsAfterDeduction > 0;

      setCalculation({
        fileCount,
        creditsNeeded,
        currentCredits,
        creditsAfterDeduction,
        creditsToPurchase,
        costInCents,
        costUSD: totalCost.toFixed(2),
        pricePerCredit: CREDIT_PRICE_USD,
        minimumPurchase: MIN_CREDITS,
        minimumEnforced,
        hasSufficientCredits
      });

      console.log('💰 Credit calculation:', {
        fileCount,
        creditsNeeded,
        currentCredits,
        creditsAfterDeduction,
        creditsToPurchase,
        costUSD: totalCost.toFixed(2),
        hasSufficientCredits
      });
    }
  }, [isOpen, fileCount, currentCredits]);

  const handlePurchaseCredits = async () => {
    if (!calculation || !user) {
      setError('Unable to proceed with purchase. Please try again.');
      return;
    }

    // Track Facebook Pixel Purchase event (November 2025 Meta best practices)
    // Using fixed content_id with quantity for better optimization & remarketing
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Purchase', {
        value: parseFloat(calculation.costUSD),
        currency: 'USD',
        content_ids: ['sortingcat_credits'],
        content_type: 'product',
        contents: [{
          id: 'sortingcat_credits',
          quantity: calculation.creditsToPurchase,
          item_price: calculation.pricePerCredit
        }],
        num_items: 1,
      });
      console.log(`📊 Facebook Pixel: Purchase tracked (modal) - ${calculation.creditsToPurchase} credits, $${calculation.costUSD}`);
    }

    setRedirecting(true);
    setError(null);

    try {
      // Get Convex HTTP Actions URL from environment (for REST endpoints)
      const convexSiteUrl = process.env.REACT_APP_CONVEX_SITE_URL;
      if (!convexSiteUrl) {
        throw new Error('Convex Site URL not configured');
      }

      const response = await fetch(`${convexSiteUrl}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clerkId: user.id,
          creditsNeeded: calculation.creditsToPurchase, // Send what UI displays (includes 1000 min)
          fileCount: calculation.fileCount,
          totalSizeBytes,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create checkout session');
      }

      const data = await response.json();
      console.log('🛒 Checkout session created:', data);

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err instanceof Error ? err.message : 'Unable to start checkout. Please try again.');
      setRedirecting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative z-50 w-full max-w-lg bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <CreditCard className="w-6 h-6 mr-2 text-blue-600" />
              Purchase Credits
            </h2>
            <button
              onClick={onClose}
              disabled={redirecting}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {error ? (
              <div className="py-8">
                <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-red-800">Error</p>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              </div>
            ) : calculation ? (
              <div className="space-y-6">
                {/* Job Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">Your Job</h3>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p>
                      <span className="font-medium">Images:</span>{' '}
                      {calculation.fileCount.toLocaleString()}
                    </p>
                    <p>
                      <span className="font-medium">Current Credits:</span>{' '}
                      {calculation.currentCredits.toLocaleString()}
                    </p>
                    <p>
                      <span className="font-medium">Credits Needed:</span>{' '}
                      {calculation.creditsNeeded.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Sufficient Credits Notice */}
                {calculation.hasSufficientCredits && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-green-900">You have enough credits!</p>
                        <p className="text-sm text-green-800 mt-1">
                          Your current balance of {calculation.currentCredits.toLocaleString()} credits
                          is sufficient for this job. You can proceed without purchasing.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Minimum Purchase Notice */}
                {calculation.minimumEnforced && !calculation.hasSufficientCredits && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-900">Minimum Purchase</p>
                        <p className="text-sm text-amber-800 mt-1">
                          The minimum purchase is {calculation.minimumPurchase.toLocaleString()}{' '}
                          credits for ${(calculation.minimumPurchase * calculation.pricePerCredit).toFixed(2)}.
                          Leftover credits will be added to your account for future use.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Purchase Summary - Only show if purchase needed */}
                {!calculation.hasSufficientCredits && (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Purchase Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Credits needed after applying balance:</span>
                        <span className="font-medium text-gray-900">
                          {calculation.creditsAfterDeduction.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Credits to purchase:</span>
                        <span className="font-medium text-gray-900">
                          {calculation.creditsToPurchase.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Price per credit:</span>
                        <span className="font-medium text-gray-900">
                          ${calculation.pricePerCredit.toFixed(4)}
                        </span>
                      </div>
                      <div className="pt-2 mt-2 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-gray-900">Total Cost:</span>
                          <span className="text-2xl font-bold text-purple-600">
                            ${calculation.costUSD}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Methods Notice */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Secure Checkout</p>
                      <p className="text-sm text-gray-600 mt-1">
                        Supports card, Apple Pay, Google Pay, and more
                      </p>
                    </div>
                  </div>
                </div>

                {/* Redirecting State */}
                {redirecting && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <Loader2 className="w-5 h-5 text-blue-600 mr-3 animate-spin" />
                      <p className="text-sm text-blue-800">
                        Redirecting to secure checkout...
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end p-6 border-t border-gray-200 space-x-3">
            <button
              onClick={onClose}
              disabled={redirecting}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {calculation?.hasSufficientCredits ? 'Close' : 'Cancel'}
            </button>
            {!calculation?.hasSufficientCredits && (
              <button
                onClick={handlePurchaseCredits}
                disabled={redirecting || !!error || !calculation}
                className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {redirecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Sign up and purchase (${calculation?.costUSD})
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseCreditsModal;
